/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
/**
 * One-tap 7-day Pro trial activation hook.
 * Floating card that appears when the user has NOT yet claimed their Pro trial.
 * Auto-dismisses once claimed. Reads /api/trial/pro-status/{sid}; writes via /api/trial/start-pro.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { XIcon, RocketLaunchIcon, SparkleIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const ProTrialHook = ({ sessionId }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem("golucru_pro_hook_dismissed"));

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/trial/pro-status/${sessionId}`).then((r) => setStatus(r.data)).catch(() => {});
  }, [sessionId]);

  const activate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/trial/start-pro", { session_id: sessionId });
      setStatus({ active: true, available: false, days_left: r.data.days_left });
      toast.success(`Pro unlocked for ${r.data.days_left} days 🚀`);
    } catch (e) {
      toast.error("Could not start trial — try again");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem("golucru_pro_hook_dismissed", "1");
    setDismissed(true);
  };

  if (!status || status.active || !status.available || dismissed) return null;

  return (
    <div
      className="glass rounded-2xl p-4 border border-[#39FF14]/30 bg-gradient-to-br from-[#39FF14]/10 to-[#00E5FF]/5 relative overflow-hidden"
      data-testid="pro-trial-hook"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-white/40 hover:text-white"
        data-testid="pro-trial-dismiss"
        title="Dismiss"
      >
        <XIcon size={14} />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <RocketLaunchIcon size={18} color="#39FF14" weight="fill" />
        <div className="font-display font-black text-sm uppercase tracking-widest">7 days of Pro, free</div>
      </div>
      <div className="text-xs text-white/70 leading-relaxed mb-3">
        Unlock smart-mode co-host, voice cloning, highlights &amp; session export. One tap. No card.
      </div>
      <button
        onClick={activate}
        disabled={busy}
        data-testid="pro-trial-activate"
        className="btn-pill btn-primary w-full justify-center text-xs disabled:opacity-50"
      >
        <SparkleIcon size={13} weight="fill" />
        {busy ? "Activating…" : "Unlock Pro — 7 days free"}
      </button>
    </div>
  );
};

export default ProTrialHook;
